<template>
  <div class="px-4 sm:px-6 lg:px-8 h-full flex flex-col">
    <!-- Popups START -->
    <AppNotification ref="notification" :type="notificationType" :title="notificationTitle" :text="notificationText" />
    <LinkExternalAccount
      v-if="hasAccounts"
      v-model="show_link_external_account"
      :account="default_account"
      @success="handleExtAccountLinked()"
      @error="
        notify('failure', 'Sorry', 'We were unable to connect your account. Please try again, if the issue persists please contact tech support.')
      "
    />
    <TransferFunds
      v-if="hasAccounts"
      v-model="show_transfer"
      @link="handleLinkEmit"
      @transfer="handleTransferSuccess"
      @error="
        notify(
          'failure',
          'Sorry',
          'We were unable to initiate your funds transfer. Please try again, if the issue persists please contact tech support.',
        )
      "
    />
    <TransactionSlideover v-model="show_transaction_slideover" :transaction="transaction" />
    <AccountsSettings v-if="hasAccounts" v-model="showSettings" />
    <AlphaFlow
      v-if="showFlow"
      v-model="showFlow"
      need="create-account"
      @completed="showFlow = false"
      @closed="showFlow = false"
      @error="showFlow = false"
    />
    <!-- Popups END -->

    <!-- Loading START -->
    <div v-if="loading" class="w-full md:h-[70vh] h-[50vh] flex items-center justify-center">
      <div class="w-24 h-24 -mt-10">
        <SpinnerFlip />
      </div>
    </div>
    <!-- Loading END -->

    <!-- No Accounts Empty State START -->
    <div v-else-if="!hasAccounts" class="flex-grow flex">
      <div class="flex-1 flex flex-col items-center p-5 w-full">
        <div
          class="bg-cover"
          style="background-image: url(https://gaosehoviibwiqrqqewu.supabase.co/storage/v1/object/public/opera_public/assets/worldMapDottedTrans.png)"
        >
          <div class="backdrop-blur-[1px]">
            <Vue3Lottie
              animation-link="https://lottie.host/ece57607-a007-459d-968f-2a960e610af5/Mum5QixDyK.json"
              :height="200"
              :width="200"
              :loop="false"
              :auto-play="true"
            />
            <div class="max-w-96 text-gray-500 text-center">
              You haven't opened an account or made any investments. Once you open your first account you will see all account activity here.
            </div>
          </div>
        </div>
        <div class="flex justify-center gap-5 mt-5">
          <AppButtonSecondary text="Open an account" size="xl" :icon="FolderPlusIcon" @click="showFlow = true" />
          <RouterLink to="investing">
            <AppButtonPrimary text="Start investing" size="xl" :icon="GlobeAmericasIcon" />
          </RouterLink>
        </div>
      </div>
    </div>
    <!-- No Accounts Empty State END -->

    <!-- Ready -->
    <div v-else class="flex-grow flex gap-5 w-full">
      <!-- Accounts Nav -->
      <div>
        <div class="bg-white rounded-xl shadow-md ring-1 ring-gray-100 w-80 flex-grow h-[85vh]">
          <AllAccountsVerticalNav
            :accounts="accountsStore.myAccounts"
            @view="handleAccountSelect"
            @link="show_link_external_account = true"
            @transfer="show_transfer = true"
            @open="showFlow = true"
            @settings="showSettings = true"
          />
        </div>
      </div>

      <!-- No Balance Empty State -->
      <div
        v-if="!selected_account_has_transactions"
        class="bg-white rounded-xl shadow-md ring-1 flex-grow w-full ring-gray-100 p-5 flex justify-center"
      >
        <div class="w-96 mt-24">
          <div
            class="bg-cover"
            style="background-image: url(https://gaosehoviibwiqrqqewu.supabase.co/storage/v1/object/public/opera_public/assets/worldMapDotted.jpg)"
          >
            <div class="backdrop-blur-[1px]">
              <Vue3Lottie
                animation-link="https://lottie.host/ece57607-a007-459d-968f-2a960e610af5/Mum5QixDyK.json"
                :height="200"
                :width="200"
                :loop="false"
                :auto-play="true"
              />
              <div v-if="is_single_account" class="max-w-96 text-gray-500 text-center">
                You haven't made any investments in this account. Once you make an investment or deposit cash into your account all your activity will
                appear here.
              </div>
              <div v-else class="max-w-96 text-gray-500 text-center">
                You haven't made any investments. Once you make an investment or deposit cash into your account all your activity will appear here.
              </div>
            </div>
          </div>
          <div class="flex justify-center gap-5 mt-5">
            <AppButtonSecondary text="Transfer" size="lg" :icon="ArrowsRightLeftIcon" @click="show_transfer = true" />
            <RouterLink to="investing">
              <AppButtonPrimary text="Invest" size="lg" :icon="GlobeAmericasIcon" />
            </RouterLink>
          </div>
        </div>
      </div>

      <!-- Account Balance -->
      <div v-else class="w-[calc(100%_-_320px)] flex flex-col gap-5">
        <div class="bg-white rounded-xl shadow-md ring-1 ring-gray-100 h-96 p-5">
          <AccountBalance :accounts="selected_accounts" />
        </div>

        <div class="flex flex-wrap gap-5">
          <div class="bg-white rounded-xl shadow-md ring-1 ring-gray-100 h-96 flex-grow min-w-[36rem]">
            <AccountActivity :accounts="selected_accounts" @transaction-details="showTransactionDetails" />
          </div>
          <div v-show="showPaperwork" class="bg-white rounded-xl shadow-md ring-1 ring-gray-100 h-96 flex-grow min-w-[36rem]">
            <AccountSubDocs :accounts="selected_accounts" @hide="showPaperwork = false" />
          </div>
          <div class="bg-white rounded-xl shadow-md ring-1 ring-gray-100 h-fit w-fit">
            <AccountAllocations :accounts="selected_accounts" />
          </div>
          <div v-show="!showPaperwork">
            <AppTooltip content="Reveal paperwork section" placement="top" class="mt-1 ml-2">
              <EyeIcon class="size-5 mt-0.5 text-gray-500 cursor-pointer" @click="showPaperwork = true" />
            </AppTooltip>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
// Essentials
import { ref, computed, onMounted } from "vue";

// Components
import SpinnerFlip from "@/components/loading/SpinnerFlip.vue";
import AppNotification from "@/components/applicationui/AppNotification.vue";
import AppTooltip from "@/components/applicationui/AppTooltip.vue";
import AppButtonPrimary from "@/components/applicationui/AppButtonPrimary.vue";
import AppButtonSecondary from "@/components/applicationui/AppButtonSecondary.vue";
import AlphaFlow from "@/components/flows/AlphaFlow.vue";
import AllAccountsVerticalNav from "@/components/custody/accounts/AllAccountsVerticalNav.vue";
import AccountBalance from "@/components/custody/accounts/AccountBalance.vue";
import AccountAllocations from "@/components/custody/accounts/AccountAllocations.vue";
import AccountActivity from "@/components/custody/accounts/AccountActivity.vue";
import LinkExternalAccount from "@/components/custody/LinkExternalAccount.vue";
import TransferFunds from "@/components/custody/TransferFunds.vue";
import TransactionSlideover from "@/components/transactions/TransactionSlideover.vue";
import AccountsSettings from "@/components/custody/AccountsSettings.vue";
import AccountSubDocs from "@/components/custody/accounts/AccountSubDocs.vue";

// Icons
import { Vue3Lottie } from "vue3-lottie";

import { ArrowsRightLeftIcon, GlobeAmericasIcon, FolderPlusIcon, EyeIcon } from "@heroicons/vue/24/outline";

// Stores
import { useAccountsStore } from "@/stores/accounts";
const accountsStore = useAccountsStore();

// Selected Account START
const hasAccounts = computed(() => {
  if (!accountsStore.myAccounts) return false;
  if (accountsStore.myAccounts.length <= 0) return false;
  return true;
});
const selected_accounts = ref([]);
const handleAccountSelect = (account) => {
  selected_accounts.value = account;
};
const selected_account_has_transactions = computed(() => {
  let has_transactions = false;
  selected_accounts.value?.forEach((account) => {
    if (account.transactions && account.transactions?.length > 0) {
      has_transactions = true;
    }
  });
  return has_transactions;
});
const is_single_account = computed(() => {
  return selected_accounts.value?.length === 1;
});
// Selected Account END

// Transactions Slideover START
const show_transaction_slideover = ref(false);
const transaction = ref(null);
const showTransactionDetails = (passed_transaction) => {
  transaction.value = passed_transaction;
  show_transaction_slideover.value = true;
};
// Transactions Slideover End

// Link External Account START
const show_link_external_account = ref(false);
const default_account = ref(null);
const handleLinkEmit = (passed_account) => {
  default_account.value = passed_account;
  show_link_external_account.value = true;
};
const handleExtAccountLinked = () => {
  notify("success", "Success", "Your external account has been linked successfully.");
  setTimeout(() => {
    window.location.reload();
  }, 3000);
};
const handleTransferSuccess = async () => {
  notify("success", "Transfer Initiated", "Your funds transfer was submitted successfully.");
  setTimeout(() => {
    window.location.reload();
  }, 3000);
};
// Link External Account END

// Transfer Funds START
const show_transfer = ref(false);
// Transfer Funds END

// Open Account START
const showFlow = ref(false);
// Open Account END

// Settings START
const showSettings = ref(false);
// Settings END

// Show Paperwork START
const showPaperwork = ref(true);
// Show Paperwork END

const notification = ref(null);
const notificationType = ref("success");
const notificationTitle = ref("Success");
const notificationText = ref("");

const notify = (type, title, text) => {
  notificationType.value = type;
  notificationTitle.value = title;
  notificationText.value = text;
  notification.value.show();
};

// Mounted START
const loading = ref(true);
onMounted(async () => {
  // Get all accounts
  await accountsStore.setMyAccounts();
  selected_accounts.value = accountsStore.myAccounts;

  // Ready
  loading.value = false;
});
// Mounted END
</script>
